import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Users, Briefcase, Mail, Download, ExternalLink, FileDown, Check, Minus } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, API } from "../lib/api";
import { Logo } from "../components/Logo";

export function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const [tab, setTab] = useState("contacts");
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    // Fetch all admin data once when the authenticated user becomes available.
    // `api` is a module singleton and setState functions are stable — React
    // does not require them in the dependency array.
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/contacts").then((r) => setContacts(r.data)),
      api.get("/admin/applications").then((r) => setApplications(r.data)),
      api.get("/admin/newsletter").then((r) => setSubscribers(r.data)),
    ])
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Admin data load failed:", err);
        toast.error("Failed to load data");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!checked) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  async function downloadResume(appId, filename) {
    try {
      const token = localStorage.getItem("albany_admin_token");
      const url = `${API}/admin/applications/${appId}/resume${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "resume";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("Download failed");
    }
  }

  function exportCsv(kind) {
    const token = localStorage.getItem("albany_admin_token");
    const url = `${API}/admin/export/${kind}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
    const a = document.createElement("a");
    a.href = url;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF7F2] text-[#0F1E3C]">
      <header className="bg-white border-b border-[#EAE3D2]">
        <div className="container-x flex items-center justify-between h-16">
          <Logo size="sm" />
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#475569] hidden sm:inline">{user.email}</span>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#EAE3D2] text-sm text-[#0F1E3C] hover:bg-[#FAF7F2]"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-x py-10">
        <h1 className="font-display font-bold text-4xl tracking-tight text-[#0F1E3C] mb-8">Dashboard</h1>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <StatCard icon={<Mail className="w-5 h-5" />} label="Contact Inquiries" value={stats?.contacts ?? "—"} />
          <StatCard icon={<Briefcase className="w-5 h-5" />} label="Applications" value={stats?.applications ?? "—"} />
          <StatCard icon={<Users className="w-5 h-5" />} label="Subscribers" value={stats?.subscribers ?? "—"} />
        </div>

        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex gap-2 p-1 border border-[#EAE3D2] rounded-full w-fit bg-white">
            {[
              { id: "contacts", label: "Contacts" },
              { id: "applications", label: "Applications" },
              { id: "subscribers", label: "Newsletter" },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                  tab === t.id ? "bg-[#0F1E3C] text-white" : "text-[#475569] hover:text-[#0F1E3C]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            data-testid={`export-csv-${tab}`}
            onClick={() => exportCsv(tab === "subscribers" ? "newsletter" : tab)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#EAE3D2] text-xs font-semibold text-[#0F1E3C] hover:bg-[#FAF7F2] hover:border-[#E9610E] transition-colors"
          >
            <FileDown className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {loading ? (
          <p className="text-[#64748B] text-sm">Loading…</p>
        ) : (
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="card-soft rounded-2xl overflow-hidden bg-white"
          >
            {tab === "contacts" && <ContactsTable items={contacts} />}
            {tab === "applications" && <ApplicationsTable items={applications} onDownload={downloadResume} />}
            {tab === "subscribers" && <SubscribersTable items={subscribers} />}
          </motion.div>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="card-soft rounded-2xl p-6 bg-white">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#FFF4EA] flex items-center justify-center text-[#E9610E]">
          {icon}
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.1em] text-[#64748B] font-semibold">{label}</div>
          <div className="font-display font-bold text-3xl mt-1 text-[#0F1E3C]">{value}</div>
        </div>
      </div>
    </div>
  );
}

function ContactsTable({ items }) {
  if (!items.length) return <Empty text="No inquiries yet." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="contacts-table">
        <thead className="border-b border-[#EAE3D2] bg-[#FAF7F2]">
          <tr className="text-left">
            <Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Type</Th><Th>Company</Th><Th>SMS</Th><Th>Message</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-[#EAE3D2] hover:bg-[#FAF7F2]">
              <Td mono>{fmtDate(c.created_at)}</Td>
              <Td>{c.name}</Td>
              <Td><a href={`mailto:${c.email}`} className="text-[#E9610E] hover:underline">{c.email}</a></Td>
              <Td>{c.phone || "—"}</Td>
              <Td><span className="border border-[#EAE3D2] rounded-full px-2 py-0.5 text-xs bg-[#FFF4EA] text-[#B45309]">{c.inquiry_type}</span></Td>
              <Td>{c.company || "—"}</Td>
              <Td><ConsentBadge given={c.sms_consent} at={c.sms_consent_at} /></Td>
              <Td><div className="max-w-md text-[#475569] whitespace-pre-wrap">{c.message}</div></Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApplicationsTable({ items, onDownload }) {
  if (!items.length) return <Empty text="No applications yet." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="applications-table">
        <thead className="border-b border-[#EAE3D2] bg-[#FAF7F2]">
          <tr className="text-left">
            <Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Position</Th><Th>Exp</Th><Th>LinkedIn</Th><Th>SMS</Th><Th>Resume</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} className="border-b border-[#EAE3D2] hover:bg-[#FAF7F2]">
              <Td mono>{fmtDate(a.created_at)}</Td>
              <Td>{a.name}</Td>
              <Td><a href={`mailto:${a.email}`} className="text-[#E9610E] hover:underline">{a.email}</a></Td>
              <Td>{a.phone || "—"}</Td>
              <Td>{a.position}</Td>
              <Td>{a.experience_years ?? "—"}</Td>
              <Td>{a.linkedin_url ? <a href={a.linkedin_url} target="_blank" rel="noreferrer" className="text-[#E9610E] inline-flex items-center gap-1">View <ExternalLink className="w-3 h-3" /></a> : "—"}</Td>
              <Td><ConsentBadge given={a.sms_consent} at={a.sms_consent_at} /></Td>
              <Td>
                {a.resume_filename ? (
                  <button
                    data-testid={`download-resume-${a.id}`}
                    onClick={() => onDownload(a.id, a.resume_filename)}
                    className="inline-flex items-center gap-1 text-[#E9610E] hover:underline"
                  >
                    <Download className="w-3 h-3" /> {a.resume_filename}
                  </button>
                ) : (
                  "—"
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConsentBadge({ given, at }) {
  if (given) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
        title={at ? `Consented at ${fmtDate(at)}` : "SMS consent given"}
      >
        <Check className="w-3 h-3" /> Yes
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-[#94A3B8] bg-[#FAF7F2] border border-[#EAE3D2] rounded-full px-2 py-0.5">
      <Minus className="w-3 h-3" /> No
    </span>
  );
}

function SubscribersTable({ items }) {
  if (!items.length) return <Empty text="No subscribers yet." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-[#EAE3D2] bg-[#FAF7F2]">
          <tr className="text-left"><Th>Date</Th><Th>Email</Th></tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-b border-[#EAE3D2] hover:bg-[#FAF7F2]">
              <Td mono>{fmtDate(s.created_at)}</Td>
              <Td>{s.email}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-xs uppercase tracking-[0.1em] text-[#64748B] font-semibold">{children}</th>;
}
function Td({ children, mono }) {
  return <td className={`px-4 py-4 align-top ${mono ? "font-mono text-xs text-[#64748B]" : "text-[#0F1E3C]"}`}>{children}</td>;
}
function Empty({ text }) {
  return <div className="p-16 text-center text-[#64748B]">{text}</div>;
}
function fmtDate(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return String(v);
  }
}
